import { DialogBase } from '@scale/dialog-base';
import { fetchTextWithTimeout, parseSectionHTML, readRecentlyViewed } from '@scale/utilities';

class ScaleSearchOverlay extends DialogBase {
  #cache = new Map();
  #fetchController;
  #recentLoaded = false;

  get holdsPage() {
    return true;
  }

  setupDialog() {
    const input = this.ref('search-input');
    input.addEventListener('input', () => this.#syncToQuery(), { signal: this.signal });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    }, { signal: this.signal });
    if (this.hasAttribute('data-no-predictive')) return;

    input.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.#navigateResults(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.#navigateResults(-1);
          break;
        case 'Enter': {
          const focused = this.ref('results')?.querySelector('.is-focused a');
          if (focused) {
            e.preventDefault();
            focused.click();
          }
          break;
        }
      }
    }, { signal: this.signal });

    input.addEventListener('input', () => {
      this.debounce('search', () => this.#onInput(), 300);
    }, { signal: this.signal });
  }

  openSearch() {
    this.open();
    this.#loadRecent();
  }

  closeSearch() {
    this.close();
  }

  searchFor(event) {
    const input = this.ref('search-input');
    input.value = event.target.closest('[data-terms]').dataset.terms;
    input.focus();
    this.#syncToQuery();
    if (this.hasAttribute('data-no-predictive')) input.form.requestSubmit();
    else this.#onInput();
  }

  clearSearch() {
    const input = this.ref('search-input');
    const results = this.ref('results');

    if (input) {
      input.value = '';
      input.focus();
    }
    this.#cancelRequest();
    if (results) {
      results.innerHTML = '';
      results.removeAttribute('aria-busy');
    }
    input?.setAttribute('aria-expanded', 'false');
    this.#syncToQuery();
  }

  onDialogClose() {
    this.#cancelRequest();
    const input = this.ref('search-input');
    if (input) {
      input.value = '';
      input.setAttribute('aria-expanded', 'false');
    }
    const results = this.ref('results');
    if (results) {
      results.innerHTML = '';
      results.removeAttribute('aria-busy');
    }
    this.#syncToQuery();
  }

  #syncToQuery() {
    const typed = this.ref('search-input').value.trim() !== '';
    const start = this.ref('start');
    const clear = this.ref('clear-btn');
    if (start) start.hidden = typed;
    if (clear) clear.hidden = !typed;
  }

  async #loadRecent() {
    const group = this.ref('recent');
    if (!group || this.#recentLoaded) return;
    this.#recentLoaded = true;

    const root = window.Shopify.routes.root;
    const products = (await Promise.all(readRecentlyViewed().slice(0, 3).map((handle) =>
      fetchTextWithTimeout(`${root}products/${handle}.js`, { signal: this.signal })
        .then(({ response, text }) => (response.ok ? JSON.parse(text) : null))
        .catch(() => null)))).filter(Boolean);
    if (!products.length) return;

    const template = this.ref('recent-item');
    this.ref('recent-list').replaceChildren(...products.map((product) => {
      const item = template.content.firstElementChild.cloneNode(true);
      item.querySelector('a').href = product.url;
      const image = item.querySelector('img');
      if (product.featured_image) {
        const src = new URL(product.featured_image, location.href);
        src.searchParams.set('width', '96');
        image.src = src.href;
      } else {
        image.remove();
      }
      item.querySelector('span').textContent = product.title;
      return item;
    }));
    group.hidden = false;
  }

  async #onInput() {
    const input = this.ref('search-input');
    const query = input?.value.trim();

    if (!query || query.length < 2) {
      this.#cancelRequest();
      const results = this.ref('results');
      if (results) {
        results.innerHTML = '';
        results.removeAttribute('aria-busy');
      }
      input?.setAttribute('aria-expanded', 'false');
      return;
    }

    const cached = this.#cache.get(query);
    if (this.#cache.has(query)) {
      this.#cancelRequest();
      this.#renderResults(cached);
      return;
    }

    const locale = window.Shopify?.routes?.root || '/';
    const url = `${locale}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,collection,page,article,query&resources[limit]=4`;

    this.#fetchController?.abort();
    const controller = new AbortController();
    this.#fetchController = controller;
    this.ref('results')?.setAttribute('aria-busy', 'true');

    try {
      const { response, text: html } = await fetchTextWithTimeout(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = parseSectionHTML(html);
      const incoming = doc.querySelector('#predictive-search-results');
      if (!incoming) throw new Error('Malformed predictive search response');
      const content = incoming.innerHTML;

      this.#cache.set(query, content);
      if (this.#cache.size > 20) {
        const oldest = this.#cache.keys().next().value;
        this.#cache.delete(oldest);
      }

      if (this.#fetchController === controller && input?.value.trim() === query) {
        this.#renderResults(content);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && this.#fetchController === controller && input?.value.trim() === query) {
        this.#renderResults('');
      }
    } finally {
      if (this.#fetchController === controller) {
        this.#fetchController = null;
        this.ref('results')?.removeAttribute('aria-busy');
      }
    }
  }

  #navigateResults(direction) {
    const results = this.ref('results');
    if (!results) return;

    const items = [...results.querySelectorAll('[role="option"]')];
    if (!items.length) return;

    const current = items.findIndex((el) => el.classList.contains('is-focused'));
    const next = Math.max(0, Math.min(items.length - 1, current + direction));

    items.forEach((el) => el.classList.remove('is-focused'));
    items[next].classList.add('is-focused');
    items[next].scrollIntoView({ block: 'nearest' });

    this.ref('search-input')?.setAttribute('aria-activedescendant', items[next].id);
  }

  #renderResults(html) {
    const results = this.ref('results');
    const input = this.ref('search-input');
    if (results) results.innerHTML = html;
    const listed = Boolean(results?.querySelector('[role="option"]'));
    results?.setAttribute('role', listed ? 'listbox' : 'region');
    input?.setAttribute('aria-expanded', String(listed));
    input?.removeAttribute('aria-activedescendant');
    results?.removeAttribute('aria-busy');
  }

  #cancelRequest() {
    this.#fetchController?.abort();
    this.#fetchController = null;
  }
}

if (!customElements.get('scale-search-overlay')) {
  customElements.define('scale-search-overlay', ScaleSearchOverlay);
}
